import {
    MenuItem,
    Checkbox,
    ListItemText,
    Select,
    Box,
    Chip,
    InputLabel,
    FormControl,
} from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import { convertToDisplayName } from '../helpers';
import { FormSelectOption } from '../types';
import { GenericFormComponentProps } from '../types';
import styles from './FormMultiSelect.module.scss';
import { useEffect } from 'react';
import { useFormSectionContext } from '../FormSection/FormSection';
import { useFormState } from 'hooks/useFormState';
interface FormMultiSelectProps extends GenericFormComponentProps {
    options: FormSelectOption[];
}

/**
 * component that allows multiple selections from a list of options
 * @param {string} field - field to track with react-hook-form
 * @param {string} label - optional label override
 * @param {boolean} disabled - is this component readonly?
 * @param {string} className - for styling
 * @param {FormSelectOption[]} options - list of options to pick from
 * @returns FormMultiSelect component
 */
export function FormMultiSelect({
    field,
    label,
    options,
    disabled,
    className,
}: FormMultiSelectProps) {
    const { control, getFieldState } = useFormContext();
    const { setFormSectionError } = useFormState();
    const { formSection } = useFormSectionContext();

    const watchedField = getFieldState(field);

    useEffect(() => {
        setFormSectionError({
            section: formSection,
            field: { name: field, error: !!watchedField.error },
        });
    }, [field, formSection, setFormSectionError, watchedField.error]);

    return (
        <Controller
            name={field}
            control={control}
            render={({ field: fieldProps, fieldState: { error } }) => {
                label = label || convertToDisplayName(field);
                const id = `form-multiselect-${field}`;
                return (
                    <FormControl
                        variant="standard"
                        className={`${styles.formMultiSelect} ${className}`}
                    >
                        <InputLabel id="demo-multiple-chip-label">
                            {label}
                        </InputLabel>
                        <Select
                            id={id}
                            value={fieldProps.value}
                            disabled={disabled}
                            variant="standard"
                            inputProps={{
                                ...fieldProps,
                                'data-testid': 'form-multiselect',
                            }}
                            multiple
                            renderValue={(selected: string[]) => (
                                <Box className={styles.chipContainer}>
                                    {selected.map((value) => {
                                        return (
                                            <Chip
                                                key={value}
                                                label={convertToDisplayName(
                                                    value
                                                )}
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                            color={error ? 'error' : 'primary'}
                            error={!!error}
                        >
                            {options.map(({ name, value }) => {
                                const label =
                                    name ?? convertToDisplayName(value);

                                return (
                                    <MenuItem
                                        key={label}
                                        value={value}
                                        data-testid="select-option"
                                    >
                                        <Checkbox
                                            checked={
                                                fieldProps.value?.indexOf(
                                                    value
                                                ) > -1
                                            }
                                        />
                                        <ListItemText primary={label} />
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                );
            }}
        />
    );
}
